import {
  type BatchAddLabelToInboxConversationsMutation,
  type FetchResult,
  InboxConversationsDocument,
  type InboxConversationsQuery,
  type MutationResult,
  useBatchAddLabelToInboxConversationsMutation,
} from "@qw/graphql";
import { KnownInboxLabel } from "@qw/qw-common";
import { useToast } from "@qw/ui";
import { useCallback } from "react";

import type { MaybePartialInboxConversation } from "../types";
import { useInboxFilter } from "./inbox-filter-provider";

interface UseBatchAddLabelToConversationsProps {
  conversations: MaybePartialInboxConversation[];
  label: string;
  successMessage?: string;
  errorMessage?: string;
}

type UseBatchAddLabelToConversationsReturn = [
  (props: UseBatchAddLabelToConversationsProps) => Promise<FetchResult<BatchAddLabelToInboxConversationsMutation>>,
  MutationResult<BatchAddLabelToInboxConversationsMutation>,
];

export const useBatchAddLabelToConversations = (): UseBatchAddLabelToConversationsReturn => {
  const { toast } = useToast();
  const { filters: conversationsFilters } = useInboxFilter();
  const [addLabelMutation, result] = useBatchAddLabelToInboxConversationsMutation({
    refetchQueries: ["InboxConversations", "InboxLabelUnreadCounts"],
  });

  const batchAddLabel = useCallback(
    async ({ conversations, label, successMessage, errorMessage }: UseBatchAddLabelToConversationsProps) => {
      const isMovingToTrash =
        !conversationsFilters.labels?.includes(KnownInboxLabel.TRASH) && label === KnownInboxLabel.TRASH;
      const isMovingFromTrash =
        conversationsFilters.labels?.includes(KnownInboxLabel.TRASH) &&
        ![KnownInboxLabel.TRASH, KnownInboxLabel.STARRED, KnownInboxLabel.UNREAD].includes(label as KnownInboxLabel);
      const canOptimisticallyUpdateRowMetadata = conversations.every(
        (conversation) =>
          "attachmentPreviews" in conversation && "draftCount" in conversation && "messageCount" in conversation
      );

      return await addLabelMutation({
        variables: { input: { conversationIds: conversations.map((conversation) => conversation.id), label } },
        update(cache, { data }) {
          const conversations = data?.batchAddLabelToInboxConversations;

          if (!conversations) {
            return;
          }

          const existingData: InboxConversationsQuery | null = cache.readQuery({
            query: InboxConversationsDocument,
            variables: { input: conversationsFilters },
          });

          const existingConversations = existingData?.inboxConversations.conversations;

          if (!existingConversations) {
            return;
          }

          const shouldRemoveFromCache = isMovingToTrash || isMovingFromTrash;

          if (shouldRemoveFromCache) {
            cache.writeQuery({
              query: InboxConversationsDocument,
              variables: { input: conversationsFilters },
              data: {
                inboxConversations: {
                  ...existingData.inboxConversations,
                  conversations: existingConversations?.filter(
                    (updatedConversation) => !conversations.some((c) => c.id === updatedConversation.id)
                  ),
                },
              },
            });
          }
        },
        ...(canOptimisticallyUpdateRowMetadata
          ? {
              optimisticResponse: () => ({
                batchAddLabelToInboxConversations: conversations.map((conversation) => {
                  const labelsSet = new Set(conversation.labels);

                  if (isMovingToTrash) {
                    labelsSet.delete("INBOX");
                  }
                  if (isMovingFromTrash) {
                    labelsSet.delete("TRASH");
                  }

                  return {
                    ...conversation,
                    attachmentPreviews: "attachmentPreviews" in conversation ? conversation.attachmentPreviews : [],
                    draftCount: "draftCount" in conversation ? conversation.draftCount : 0,
                    messageCount: "messageCount" in conversation ? conversation.messageCount : 0,
                    labels: [...labelsSet, label],
                  };
                }),
              }),
            }
          : {}),
        onCompleted: () => {
          if (successMessage) {
            toast({
              variant: "default",
              title: successMessage,
            });
          }
        },
        onError: () => {
          if (errorMessage) {
            toast({
              variant: "destructive",
              title: errorMessage,
            });
          }
        },
      });
    },
    [addLabelMutation, conversationsFilters, toast]
  );

  return [batchAddLabel, result];
};
